class TieredProgress extends HTMLElement {
  static get observedAttributes() {
    return ['data-settings'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.settings = {};
    this.currentValue = 0;
    this._max = parseInt(this.getAttribute('max') || '100');
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback(name) {
    if (name === 'data-settings') {
      this.loadSettings();
      this.render();
    }
  }

  loadSettings() {
    try {
      const settingsString = this.getAttribute('data-settings');
      if (!settingsString) {
        this.settings = {};
        return;
      }
      
      // First, unescape the HTML entities
      const textarea = document.createElement('textarea');
      textarea.innerHTML = settingsString;
      const unescapedString = textarea.value;
      
      // Then parse the JSON
      this.settings = JSON.parse(unescapedString);
      this.currentValue = parseFloat(this.settings.currentValue || 0);
    } catch (e) {
      console.error('Error loading tiered progress settings:', e);
      console.error('Settings string:', this.getAttribute('data-settings'));
      this.settings = {};
    }
  }

  getProgressPercentage() {
    if (!this.settings.nextTierMin) return 100;
    return Math.min((this.currentValue / this.settings.nextTierMin) * 100, 100);
  }

  getProgressText() {
    const { title, achievedText, currentTier, nextTier, nextTierMin } = this.settings;
    if (nextTier && nextTierMin) {
      const amount = (nextTierMin - this.currentValue).toFixed(2);
      return title
        .replace('{amount}', `$${amount}`)
        .replace('{tier}', nextTier);
    }
    
    if (currentTier) {
      return achievedText.replace('{tier}', currentTier);
    }
    
    return '';
  }

  updateFromCart(cart) {
    this.currentValue = (cart.total_price / 100).toFixed(2);
    this.render();
  }

  setupEventListeners() {
    // Listen for cart updates if available in your theme
    if (window.PUB_SUB_EVENTS?.cartUpdate) {
      document.addEventListener(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.detail?.cartData) {
          this.updateFromCart(event.detail.cartData);
        }
      });
    }
  }

  render() {
    const progress = this.getProgressPercentage();
    const progressText = this.getProgressText();
    
    // Debug logging
    console.log('Rendering progress bar with:', {
      progress,
      progressText,
      settings: this.settings
    });
    
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
          --progress-color: ${this.settings?.progressColor || '#4CAF50'};
          --bg-color: ${this.settings?.bgColor || '#F5F5F5'};
          --text-color: ${this.settings?.textColor || '#333333'};
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          padding: 0 0 1rem;
          margin: 0 0 1rem;
        }
        
        .progress-container {
          width: 100%;
          height: 4px;
          overflow: hidden;
          position: relative;
          margin: 0.5rem 0 1rem;
        }
        
        .progress-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: var(--progress-color);
          opacity: 0.3;
          z-index: -1;
        }
        
        .progress-bar {
          height: 100%;
          background-color: var(--progress-color);
          width: ${progress}%;
          transition: width 0.3s ease;
          position: relative;
        }
        
        .progress-text {
          text-align: center;
          font-size: 1.2rem;
          color: var(--bg-color);
          margin: 0.75rem 0 0.25rem;
          min-height: 1.5rem;
          font-weight: 500;
          line-height: 1.4;
          font-family: var(--font-heading-family);
        }
      </style>
      
      <div class="progress-container">
        <div class="progress-bar"></div>
      </div>
      <div class="progress-text">${progressText}</div>
    `;
  }
}

// Define the custom element
if (!customElements.get('tiered-progress')) {
  customElements.define('tiered-progress', TieredProgress);
}
